"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Modal } from "@/components/modal";
import { ClearableInput } from "@/components/clearable-input";

export interface ConfirmOptions {
  message: string;
  title?: string;
  confirmText?: string;
  cancelText?: string;
  /** Red confirm button for destructive actions (deletes, cancels). */
  danger?: boolean;
  /** When set, renders a checkbox with this label below the message. */
  checkbox?: string;
  /** When true, Confirm stays disabled until the checkbox is checked.
   *  Default false (optional “don't show again” style). */
  checkboxRequired?: boolean;
  /** Called on settle with the checkbox's final state (false if unchecked or
      no checkbox). Fires for both confirm and cancel — read it only when the
      result is what you act on. */
  onCheckbox?: (checked: boolean) => void;
  /**
   * When set, the user must type this exact string (case-insensitive, trimmed)
   * into an in-modal field before Confirm is enabled. Use for irreversible
   * deletes instead of `window.prompt` — never use native browser dialogs.
   */
  typeToConfirm?: string;
  /** Placeholder / label above the type-to-confirm field. */
  typeToConfirmLabel?: string;
}

// Call with a plain string for a quick yes/no, or an options object for control.
export type ConfirmArg = string | ConfirmOptions;

export type ConfirmFn = (arg: ConfirmArg) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(async () => false);

/** Promise-based confirm. `if (!(await confirm("Delete?"))) return;`
 *  Always uses the app Modal motif — never `window.confirm` / `window.prompt`. */
export function useConfirm(): ConfirmFn {
  return useContext(ConfirmContext);
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const [checked, setChecked] = useState(false);
  const [typed, setTyped] = useState("");
  const resolveRef = useRef<((ok: boolean) => void) | null>(null);
  // Ref mirrors so `settle` reads latest values without stale closures.
  const checkedRef = useRef(false);
  const typedRef = useRef("");
  const optsRef = useRef<ConfirmOptions | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const confirm = useCallback<ConfirmFn>((arg) => {
    const o = typeof arg === "string" ? { message: arg } : arg;
    optsRef.current = o;
    checkedRef.current = false;
    typedRef.current = "";
    setChecked(false);
    setTyped("");
    setOpts(o);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    optsRef.current?.onCheckbox?.(checkedRef.current);
    resolveRef.current?.(ok);
    resolveRef.current = null;
    optsRef.current = null;
    setOpts(null);
    setTyped("");
    typedRef.current = "";
  }, []);

  const typeRequired = opts?.typeToConfirm?.trim() ?? "";
  const typeOk =
    !typeRequired ||
    typed.trim().toLowerCase() === typeRequired.toLowerCase();
  const checkOk = !opts?.checkboxRequired || checked;
  const canConfirm = typeOk && checkOk;

  // Focus the type-to-confirm field (or the primary action) when the dialog opens.
  useEffect(() => {
    if (!opts) return;
    if (opts.typeToConfirm) {
      // Next tick so the input is mounted.
      const t = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(t);
    }
  }, [opts]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {/* zIndex above other modals (Modal default 50) so a confirm raised from
          within a modal sits on top; ESC / backdrop click resolves to false. */}
      <Modal open={opts != null} onClose={() => settle(false)} width="420px" zIndex={70}>
        {opts && (
          <div>
            {opts.title && (
              <h3 className="text-sm font-semibold text-foreground mb-2">{opts.title}</h3>
            )}
            <p className="text-sm text-muted whitespace-pre-line mb-4">{opts.message}</p>
            {opts.checkbox && (
              <label className="flex items-center gap-2 text-xs text-muted mb-4 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    checkedRef.current = e.target.checked;
                    setChecked(e.target.checked);
                  }}
                  className="cursor-pointer"
                />
                {opts.checkbox}
              </label>
            )}
            {opts.typeToConfirm && (
              <div className="mb-4">
                <p className="text-xs text-muted mb-1.5">
                  {opts.typeToConfirmLabel ??
                    `Type “${opts.typeToConfirm}” to confirm`}
                </p>
                <ClearableInput
                  ref={inputRef}
                  type="text"
                  value={typed}
                  onChange={(e) => {
                    typedRef.current = e.target.value;
                    setTyped(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && canConfirm) {
                      e.preventDefault();
                      settle(true);
                    }
                  }}
                  className="w-full rounded-md border border-border bg-surface-light px-3 py-[6px] text-sm text-foreground placeholder-muted focus:outline-none focus:border-cyan focus:ring-1 focus:ring-cyan font-mono"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => settle(false)}
                className="text-xs font-medium px-3 py-1.5 rounded bg-surface-light text-muted hover:text-foreground transition-colors"
              >
                {opts.cancelText ?? "Cancel"}
              </button>
              <button
                type="button"
                autoFocus={!opts.typeToConfirm}
                disabled={!canConfirm}
                onClick={() => settle(true)}
                className={`text-xs font-medium px-3 py-1.5 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  opts.danger
                    ? "bg-red/20 text-red hover:bg-red/30"
                    : "bg-cyan/20 text-cyan hover:bg-cyan/30"
                }`}
              >
                {opts.confirmText ?? "Confirm"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}
